import Component from '@glimmer/component'
import { service } from '@ember/service'
import type RouterService from '@ember/routing/router-service'
import { BasePageButton, BasePageSelectorTraverser } from './traverser'
import { not, eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { fn } from '@ember/helper'
import { optional } from 'teacher-ui-client/helpers/optional'
import { get } from '@ember/object'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import PagingInfo from 'teacher-ui-client/components/base/page-selector/paging-info'
import { getOwner } from '@ember/owner'
import type Route from '@ember/routing/route'

/**
 * Query Param based paging (recommended) - most values read from the URL.
 * <Base::PageSelector @pageCount={{this.pageCount}} />
 *
 * Action based paging - all values need to be supplied.
 * <Base::PageSelector @pageCount={{this.pageCount}} @currentPage={{this.currentPage}} @changePage={{this.updatePage}} />
 */
interface Args {
  /**
   * The total number of pages. This changes per request and can't be retrieved via the URL.
   */
  pageCount: number
  /**
   * Provide this if your Query Param key for page number is not `pageNumber`
   */
  pageNumberKey?: string
  /**
   * Provide the currentPage if it is not available via Query Params.
   */
  currentPage?: number | string
  /**
   * Provide this if you don't want to use pagination via Query Params.
   */
  changePage?: (pageNumber: number) => unknown

  /**
   * The total number of records, if available. This is used to display the number of records in the paging info.
   */
  itemCount?: number

  /**
   * Provide this if your Query Param key for page number is not `perPage`
   */
  itemsPerPageKey?: string

  /**
   * The number of records per page if it is not available via Query Params, if available. This is used to display the number of records in the paging info.
   */
  itemsPerPage?: number

  /**
   * Provide this if you want to change the number of items per page.
   */
  changePerPage?: (itemsPerPage: number) => void

  /**
   * If true, the change per page dropdown will not be displayed. Some usages have specific per-page values that are not changeable.
   */
  lockPerPage?: boolean
}

interface Signature {
  Element: HTMLElement
  Args: Args
}

interface PageLink {
  pageNumber: number
  query: Record<string, unknown>
}

function toInt(maybeString: string | number) {
  return typeof maybeString === 'string' ? parseInt(maybeString, 10) : maybeString
}

export class BasePageSelector extends Component<Signature> {
  @service router!: RouterService

  get pageNumberKey() {
    return this.args.pageNumberKey ?? 'pageNumber'
  }

  get itemsPerPageKey() {
    return this.args.itemsPerPageKey ?? 'perPage'
  }

  /**
   * Note that the "raw" page query param when obtained via the router service will always come through as a string, even if
   * the default value in the controller is a number - it does not cast the value to a number when obtained this way, as.
   * opposed to accessing it via the controller.
   *
   * However, this component will always deal with pages as numbers.
   */
  get currentPage(): number {
    // Current Page via arguments
    if (this.args.currentPage) {
      return toInt(this.args.currentPage)
    }

    // Current Page via Query Params
    return this.getQueryParamFor(this.pageNumberKey) ?? 1
  }

  get itemsPerPage(): number | undefined {
    // Items per page via arguments
    if (this.args.itemsPerPage) {
      return this.args.itemsPerPage
    }

    // Items per page via Query Params
    return this.getQueryParamFor(this.itemsPerPageKey)
  }

  /**
   * This function does the heavy lifting of the routing logic to figure out current values for things from the QPs
   */
  getQueryParamFor(key: string): number | undefined {
    // First, try to get it from the router service query params. This will work if perPage is in the URL.
    let value = this.router.currentRoute?.queryParams[key]
    if (typeof value === 'string' || typeof value === 'number') {
      return toInt(value)
    }

    /* The query param we want isn't set yet. We need to determine the default value of the query param we want.
     * The router service can only provide data that is in the URL (and default values often aren't in the URL), so we
     * need to use Route.paramsFor() where this information is available.
     *
     * We iterate over each routeInfo in the hierarchy, bottom to top, and call paramsFor() on it. Note that rInfo
     * provides a find() method, but that traverses from top to bottom, whereas this for loop is bottom to top.
     */
    const owner = getOwner(this)
    const applicationRoute = owner?.lookup('route:application') as Route | undefined
    if (!applicationRoute) return
    for (let rInfo = this.router.currentRoute; rInfo && !value; rInfo = rInfo.parent) {
      value = applicationRoute.paramsFor(rInfo.name)[key]
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return toInt(value)
    }
  }

  get prevPageQuery() {
    if (this.currentPage <= 1) {
      return undefined
    } else {
      return { [this.pageNumberKey]: this.currentPage - 1 }
    }
  }

  get nextPageQuery() {
    if (this.currentPage >= this.args.pageCount) {
      return undefined
    } else {
      return { [this.pageNumberKey]: this.currentPage + 1 }
    }
  }

  get pageLinks(): (PageLink | null)[] {
    return this.generatePageLinks()
  }

  get showArrowTraversers() {
    return this.args.pageCount > 1
  }

  /**
   * Generates a stable array of page link items, that should return the same number of pages (desiredPageCount) as the
   * user traverses the pager. Extra pages are ellipsed (indicated by nulls)
   */
  generatePageLinks(): (PageLink | null)[] {
    const firstPage = 1
    const desiredPageCount = 9
    const { pageCount: lastPage } = this.args
    const { currentPage, pageNumberKey, itemsPerPage, itemsPerPageKey } = this

    // [1, 2, 3, ... pageCount]
    const pageRange = Array.from({ length: this.args.pageCount }, (_v, index) => index + 1)

    const buildPageLink = (pageNumber: number): PageLink => ({
      pageNumber,
      query: { [pageNumberKey]: pageNumber, [itemsPerPageKey]: itemsPerPage },
    })

    // If number of pages is under the max allowed, just return links for all those pages.
    if (pageRange.length <= desiredPageCount) return pageRange.map(buildPageLink)

    // Else, we need to determine which pages should be hidden by ellipses, and which are safe.
    const safePageNumbers = new Set<number>()
    safePageNumbers.add(firstPage)
    safePageNumbers.add(lastPage)
    safePageNumbers.add(currentPage)

    // With maxPages 9, the border zone is 1,2,3,4 vs 8,9,10,11
    // If the current page is within these pages, only 1 ellipsis will be shown.
    const borderZoneSize = Math.floor(desiredPageCount / 2)
    const currentPageInBorderZone = currentPage < firstPage + borderZoneSize || currentPage > lastPage - borderZoneSize
    const expectedEllipsisCount = currentPageInBorderZone ? 1 : 2

    // keep adding safe numbers either side of currentpage until reaching maxpages (minus expected count of ellipses)
    for (let i = 1; safePageNumbers.size < desiredPageCount - expectedEllipsisCount; i++) {
      const leftAdjacent = currentPage - i
      const rightAdjacent = currentPage + i
      if (pageRange.includes(leftAdjacent)) safePageNumbers.add(leftAdjacent)
      if (safePageNumbers.size < desiredPageCount - expectedEllipsisCount) {
        if (pageRange.includes(rightAdjacent)) safePageNumbers.add(rightAdjacent)
      }
    }

    // Iterate over sorted safe page numbers, to find any gaps of 1 (diff between two adjacent numbers is 2)
    // Add those to the set as well, this way there are no ellipses used to hide a single page number (no point)
    // This shouldn't affect the final pages count.
    ;[...safePageNumbers]
      .sort((a, b) => a - b)
      .forEach((value, index, arr) => {
        if (arr[index + 1] - value === 2) safePageNumbers.add(value + 1)
      })

    return pageRange.reduce<(PageLink | null)[]>((pageLinks: (PageLink | null)[], pageNumber: number) => {
      if (safePageNumbers.has(pageNumber)) {
        return [...pageLinks, buildPageLink(pageNumber)]
      } else {
        return pageLinks[pageLinks.length - 1] === null ? pageLinks : [...pageLinks, null]
      }
    }, [])
  }

  /**
   * Returns true if pageNumber is or adjacent to the comparePageNumber
   *
   * For example, if the pageNumber were 4, then 3, 4 and 5 are adjacent if the distance is 1.
   */
  isAdjacentToPage(pageNumber: number, comparePageNumber: number, distance: number) {
    return pageNumber >= comparePageNumber - distance && pageNumber <= comparePageNumber + distance
  }

  <template>
    {{#if @itemCount}}
      {{! If no item count is provided, we assume there are no items to display, so we hide the page selector }}
      <nav class="print:hidden" data-test-page-selector ...attributes>
        <PagingInfo
          @pageCount={{@pageCount}}
          @currentPage={{this.currentPage}}
          @itemCount={{@itemCount}}
          @itemsPerPage={{this.itemsPerPage}}
          @changePerPage={{@changePerPage}}
          @lockPerPage={{@lockPerPage}}
          @pageNumberKey={{this.pageNumberKey}}
          @itemsPerPageKey={{this.itemsPerPageKey}}
        />
        <div class="flex justify-center gap-x-1">
          {{#if this.showArrowTraversers}}
            <BasePageSelectorTraverser
              aria-label={{t "base.pageSelector.prev"}}
              @isDisabled={{not this.prevPageQuery}}
              @route={{if this.router.currentRouteName this.router.currentRouteName}}
              @query={{this.prevPageQuery}}
              @onClick={{if @changePage (fn (optional @changePage) (get this.prevPageQuery this.pageNumberKey))}}
              @iconOnly={{true}}
            >
              <PhosphorIcon @icon="phosphor-caret-left-bold" class="h-5 w-5" />
            </BasePageSelectorTraverser>
          {{/if}}
          {{#each this.pageLinks as |pageLink|}}
            {{#if pageLink}}
              <BasePageSelectorTraverser
                aria-label={{pageLink.pageNumber}}
                @isActive={{eq this.currentPage pageLink.pageNumber}}
                @route={{if this.router.currentRouteName this.router.currentRouteName}}
                @query={{pageLink.query}}
                @onClick={{if @changePage (fn (optional @changePage) pageLink.pageNumber)}}
              >
                {{pageLink.pageNumber}}
              </BasePageSelectorTraverser>
            {{else}}
              <BasePageButton @iconOnly={{true}} aria-label="..." disabled={{true}}>
                <PhosphorIcon @icon="phosphor-dots-three" class="h-5 w-5" />
              </BasePageButton>
            {{/if}}
          {{/each}}
          {{#if this.showArrowTraversers}}
            <BasePageSelectorTraverser
              aria-label={{t "base.pageSelector.next"}}
              @isDisabled={{not this.nextPageQuery}}
              @route={{if this.router.currentRouteName this.router.currentRouteName}}
              @query={{this.nextPageQuery}}
              @onClick={{if @changePage (fn (optional @changePage) (get this.nextPageQuery this.pageNumberKey))}}
              @iconOnly={{true}}
            >
              <PhosphorIcon @icon="phosphor-caret-right-bold" class="h-5 w-5" />
            </BasePageSelectorTraverser>
          {{/if}}
        </div>
      </nav>
    {{/if}}
  </template>
}

export default BasePageSelector
